import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Percent,
  Plus,
  Users,
  FileText,
  Mail,
  Download,
  Calendar,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  X,
  Loader2
} from 'lucide-react'
import { format, startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns'
import { sv } from 'date-fns/locale'
import toast from 'react-hot-toast'
import { useAuth } from '../../lib/auth'
import {
  fetchProvisionPartners,
  createProvisionPartner,
  updateProvisionPartner,
  deleteProvisionPartner,
  fetchProvisionPayouts,
  createProvisionPayout,
  updateProvisionPayout,
  deleteProvisionPayout,
  createProvisionReportDraft,
  markProvisionReportSent,
  calculateProvisions,
  getCustomerCountsPerPartner,
  formatPeriod,
  type ProvisionPartner,
  type ProvisionPayout,
  type ProvisionSummary
} from '../../services/provisions'
import { generateProvisionPDF, generateProvisionPDFFromData } from '../../services/provisionPdf'

type TabId = 'reports' | 'partners' | 'invoices'

export function Provisions() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('reports')
  const [loading, setLoading] = useState(true)

  // Partners state
  const [partners, setPartners] = useState<ProvisionPartner[]>([])
  const [customerCounts, setCustomerCounts] = useState<Record<string, number>>({})
  const [showPartnerModal, setShowPartnerModal] = useState(false)
  const [editingPartner, setEditingPartner] = useState<ProvisionPartner | null>(null)
  const [partnerFormData, setPartnerFormData] = useState({
    name: '',
    email: '',
    percentage: 10,
    notes: '',
    is_active: true
  })

  // Reports state - default to current month
  const [dateFrom, setDateFrom] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'))
  const [dateTo, setDateTo] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'))
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('')
  const [provisionSummaries, setProvisionSummaries] = useState<ProvisionSummary[]>([])
  const [expandedPartners, setExpandedPartners] = useState<Set<string>>(new Set())
  const [calculatingProvisions, setCalculatingProvisions] = useState(false)

  // Invoices state
  const [payouts, setPayouts] = useState<(ProvisionPayout & { partner_name: string })[]>([])
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [invoiceFormData, setInvoiceFormData] = useState({
    provision_partner_id: '',
    period_year: new Date().getFullYear(),
    period_month: new Date().getMonth(), // Previous month
    amount: 0,
    invoice_number: '',
    notes: ''
  })

  // Load initial data
  useEffect(() => {
    loadData()
  }, [])

  // Calculate provisions when date range or partner changes
  useEffect(() => {
    if (activeTab === 'reports' && dateFrom && dateTo) {
      loadProvisions()
    }
  }, [activeTab, dateFrom, dateTo, selectedPartnerId])

  const loadData = async () => {
    setLoading(true)
    try {
      const [partnersData, countsData, payoutsData] = await Promise.all([
        fetchProvisionPartners(true),
        getCustomerCountsPerPartner(),
        fetchProvisionPayouts()
      ])
      setPartners(partnersData)
      setCustomerCounts(countsData)
      setPayouts(payoutsData)
    } catch (error) {
      console.error('Error loading provision data:', error)
      toast.error('Kunde inte ladda provisionsdata')
    } finally {
      setLoading(false)
    }
  }

  const loadProvisions = async () => {
    setCalculatingProvisions(true)
    try {
      const summaries = await calculateProvisions(
        dateFrom,
        dateTo,
        selectedPartnerId || undefined
      )
      setProvisionSummaries(summaries)
    } catch (error) {
      console.error('Error calculating provisions:', error)
      toast.error('Kunde inte beräkna provision')
    } finally {
      setCalculatingProvisions(false)
    }
  }

  // Partner modal handlers
  const openPartnerModal = (partner?: ProvisionPartner) => {
    if (partner) {
      setEditingPartner(partner)
      setPartnerFormData({
        name: partner.name,
        email: partner.email || '',
        percentage: partner.percentage,
        notes: partner.notes || '',
        is_active: partner.is_active
      })
    } else {
      setEditingPartner(null)
      setPartnerFormData({
        name: '',
        email: '',
        percentage: 10,
        notes: '',
        is_active: true
      })
    }
    setShowPartnerModal(true)
  }

  const savePartner = async () => {
    if (!partnerFormData.name) {
      toast.error('Namn är obligatoriskt')
      return
    }

    try {
      if (editingPartner) {
        await updateProvisionPartner(editingPartner.id, partnerFormData)
        toast.success('Partner uppdaterad')
      } else {
        await createProvisionPartner(partnerFormData)
        toast.success('Partner skapad')
      }
      setShowPartnerModal(false)
      loadData()
    } catch (error) {
      console.error('Error saving partner:', error)
      toast.error('Kunde inte spara partner')
    }
  }

  const handleDeletePartner = async (partner: ProvisionPartner) => {
    const count = customerCounts[partner.id] || 0
    if (count > 0) {
      toast.error(`Kan inte ta bort - ${count} kunder är kopplade till denna partner`)
      return
    }

    if (!confirm(`Är du säker på att du vill ta bort ${partner.name}?`)) {
      return
    }

    try {
      await deleteProvisionPartner(partner.id)
      toast.success('Partner borttagen')
      loadData()
    } catch (error) {
      console.error('Error deleting partner:', error)
      toast.error('Kunde inte ta bort partner')
    }
  }

  // Invoice modal handlers
  const [editingPayoutId, setEditingPayoutId] = useState<string | null>(null)

  const openInvoiceModal = (payout?: ProvisionPayout & { partner_name: string }) => {
    if (payout) {
      // Editing existing payout - register received invoice
      setEditingPayoutId(payout.id)
      setInvoiceFormData({
        provision_partner_id: payout.provision_partner_id,
        period_year: payout.period_year,
        period_month: payout.period_month,
        amount: payout.amount,
        invoice_number: payout.invoice_number || '',
        notes: payout.notes || ''
      })
    } else {
      // Creating new (should not happen in new flow, but keep for safety)
      setEditingPayoutId(null)
      const prevMonth = subMonths(new Date(), 1)
      setInvoiceFormData({
        provision_partner_id: partners[0]?.id || '',
        period_year: prevMonth.getFullYear(),
        period_month: prevMonth.getMonth() + 1,
        amount: 0,
        invoice_number: '',
        notes: ''
      })
    }
    setShowInvoiceModal(true)
  }

  const saveInvoice = async () => {
    if (!invoiceFormData.invoice_number) {
      toast.error('Ange fakturanummer')
      return
    }

    try {
      if (editingPayoutId) {
        // Update existing payout with invoice info
        await updateProvisionPayout(editingPayoutId, {
          invoice_number: invoiceFormData.invoice_number,
          invoice_received_at: new Date().toISOString(),
          notes: invoiceFormData.notes || null
        })
        toast.success('Faktura registrerad')
      } else {
        // Create new payout (legacy flow)
        await createProvisionPayout({
          provision_partner_id: invoiceFormData.provision_partner_id,
          period_year: invoiceFormData.period_year,
          period_month: invoiceFormData.period_month,
          amount: invoiceFormData.amount,
          invoice_number: invoiceFormData.invoice_number || null,
          invoice_received_at: new Date().toISOString(),
          report_sent_at: null,
          report_data: null,
          notes: invoiceFormData.notes || null
        })
        toast.success('Faktura registrerad')
      }
      setShowInvoiceModal(false)
      setEditingPayoutId(null)
      loadData()
      loadProvisions()
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error('Det finns redan en faktura registrerad för denna period')
      } else {
        console.error('Error saving invoice:', error)
        toast.error('Kunde inte registrera faktura')
      }
    }
  }

  const handleDeletePayout = async (payout: ProvisionPayout & { partner_name: string }) => {
    if (!confirm(`Är du säker på att du vill ta bort posten för ${formatPeriod(payout.period_year, payout.period_month)}?`)) {
      return
    }

    try {
      await deleteProvisionPayout(payout.id)
      toast.success('Post borttagen')
      loadData()
      loadProvisions()
    } catch (error: any) {
      console.error('Error deleting payout:', error)
      if (error.message?.includes('redan skickats')) {
        toast.error('Kan inte ta bort - underlag har redan skickats')
      } else {
        toast.error('Kunde inte ta bort post')
      }
    }
  }

  // Prepare and download provision report (creates draft)
  const handleSendReport = async (summary: ProvisionSummary) => {
    try {
      const dateFromObj = new Date(dateFrom)
      const dateToObj = new Date(dateTo)

      // Save report data as draft
      await createProvisionReportDraft(summary, dateFrom, dateTo)

      // Generate PDF
      await generateProvisionPDF(summary, dateFromObj, dateToObj)

      // Open mailto link
      const email = summary.partner_email
      if (email) {
        const periodMonth = format(dateFromObj, 'MMMM yyyy', { locale: sv })
        const periodRef = `PROV-${format(dateFromObj, 'yyyy-MM')}`
        const subject = encodeURIComponent(`Provisionsunderlag ${periodMonth} - MB Trädgård Support`)
        const userName = profile?.full_name || ''
        const body = encodeURIComponent(
          `Hej,\n\nBifogat finns provisionsunderlag för ${periodMonth}.\n\n` +
          `Underlagsnr: ${periodRef}\n\n` +
          `Provisionsgrundande: ${Math.round(summary.total_provision_base).toLocaleString('sv-SE')} kr\n` +
          `Provision (${summary.partner_percentage}%): ${Math.round(summary.total_provision).toLocaleString('sv-SE')} kr\n\n` +
          `Vänligen ange underlagsnr ${periodRef} på fakturan.\n\n` +
          `Med vänliga hälsningar,\n${userName}\nMB Trädgård Support AB`
        )
        window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank')
      }

      toast.success('PDF nedladdad - markera som skickad i Fakturor-fliken efter att du skickat mejlet')
      loadData() // Reload to show the new payout record
    } catch (error: any) {
      console.error('Error preparing report:', error)
      if (error.message?.includes('redan skickats')) {
        toast.error('Underlag har redan skickats för denna period')
      } else {
        toast.error('Kunde inte förbereda underlag')
      }
    }
  }

  // Mark report as sent
  const handleMarkAsSent = async (payout: ProvisionPayout & { partner_name: string }) => {
    try {
      await markProvisionReportSent(payout.id)
      toast.success('Underlag markerat som skickat')
      loadData()
    } catch (error) {
      console.error('Error marking report as sent:', error)
      toast.error('Kunde inte markera som skickat')
    }
  }

  // View stored report and regenerate PDF
  const handleViewReport = async (payout: ProvisionPayout & { partner_name: string }) => {
    if (!payout.report_data) {
      toast.error('Ingen underlagsdata sparad')
      return
    }

    try {
      await generateProvisionPDFFromData(payout.report_data)
      toast.success('PDF genererad från sparat underlag')
    } catch (error) {
      console.error('Error generating PDF from stored data:', error)
      toast.error('Kunde inte generera PDF')
    }
  }

  const togglePartnerExpanded = (partnerId: string) => {
    setExpandedPartners(prev => {
      const newSet = new Set(prev)
      if (newSet.has(partnerId)) {
        newSet.delete(partnerId)
      } else {
        newSet.add(partnerId)
      }
      return newSet
    })
  }

  // Month navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    const currentFrom = new Date(dateFrom)
    const newMonth = direction === 'prev' ? subMonths(currentFrom, 1) : addMonths(currentFrom, 1)
    setDateFrom(format(startOfMonth(newMonth), 'yyyy-MM-dd'))
    setDateTo(format(endOfMonth(newMonth), 'yyyy-MM-dd'))
  }

  // Get current period display
  const getCurrentPeriodDisplay = () => {
    const from = new Date(dateFrom)
    return format(from, 'MMMM yyyy', { locale: sv })
  }

  const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
    { id: 'reports', label: 'Underlag', icon: FileText },
    { id: 'partners', label: 'Partners', icon: Users },
    { id: 'invoices', label: 'Fakturor', icon: Mail }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nature-600 mx-auto"></div>
          <p className="mt-4 text-nature-600 dark:text-nature-400">Laddar provisionsdata...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <Percent className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Provision</h1>
            <p className="text-sm text-nature-500 dark:text-nature-400">
              Hantera provisioner till samarbetspartners
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-nature-200 dark:border-nature-700">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-600 dark:text-amber-400'
                  : 'border-transparent text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {partners.filter(p => p.is_active).map(partner => {
              const summary = provisionSummaries.find(s => s.partner_id === partner.id)
              const provisionPercentOfTotal = summary && summary.total_sales > 0
                ? ((summary.total_provision / summary.total_sales) * 100).toFixed(1)
                : '0'
              return (
                <div
                  key={partner.id}
                  className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-nature-900 dark:text-nature-100">{partner.name}</h3>
                    <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">{partner.percentage}%</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-nature-600 dark:text-nature-400">
                      <span>Kunder</span>
                      <span>{customerCounts[partner.id] || 0}</span>
                    </div>
                    <div className="border-t border-nature-200 dark:border-nature-700 pt-2 mt-2">
                      <div className="flex justify-between text-nature-600 dark:text-nature-400">
                        <span>Totalt sålt</span>
                        <span className="text-nature-900 dark:text-nature-100">
                          {summary ? `${summary.total_sales.toLocaleString('sv-SE')} kr` : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between text-nature-600 dark:text-nature-400">
                        <span>Provisionsgrund.</span>
                        <span className="text-nature-900 dark:text-nature-100">
                          {summary ? `${Math.round(summary.total_provision_base).toLocaleString('sv-SE')} kr` : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between text-nature-600 dark:text-nature-400 mt-1 pt-1 border-t border-nature-100 dark:border-nature-600">
                        <span className="font-medium">Provision</span>
                        <span className="font-semibold text-amber-600 dark:text-amber-400">
                          {summary ? `${Math.round(summary.total_provision).toLocaleString('sv-SE')} kr` : '-'}
                        </span>
                      </div>
                      {summary && summary.total_sales > 0 && (
                        <div className="flex justify-between text-nature-500 dark:text-nature-500 text-xs mt-1">
                          <span>Provisionskostnad</span>
                          <span>{provisionPercentOfTotal}% av total</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Filters */}
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Month navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 text-nature-600 dark:text-nature-400 transition-colors"
                  title="Föregående månad"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-sm font-medium text-nature-700 dark:text-nature-300 min-w-[120px] text-center capitalize">
                  {getCurrentPeriodDisplay()}
                </span>
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-1.5 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700 text-nature-600 dark:text-nature-400 transition-colors"
                  title="Nästa månad"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              <div className="h-6 w-px bg-nature-200 dark:bg-nature-700 hidden sm:block" />

              {/* Date range pickers */}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-nature-500" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="input-field py-1.5"
                />
                <span className="text-nature-500">-</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="input-field py-1.5"
                />
              </div>

              <div className="h-6 w-px bg-nature-200 dark:bg-nature-700 hidden sm:block" />

              <select
                value={selectedPartnerId}
                onChange={(e) => setSelectedPartnerId(e.target.value)}
                className="input-field py-1.5"
              >
                <option value="">Alla partners</option>
                {partners.filter(p => p.is_active).map(partner => (
                  <option key={partner.id} value={partner.id}>{partner.name}</option>
                ))}
              </select>
              {calculatingProvisions && (
                <Loader2 className="h-5 w-5 animate-spin text-nature-500" />
              )}
            </div>
          </div>

          {/* Provision details */}
          <div className="space-y-4">
            {provisionSummaries.length === 0 ? (
              <div className="text-center py-12 bg-[#faf9f7] dark:bg-nature-800 rounded-lg border border-nature-200 dark:border-nature-700">
                <FileText className="h-12 w-12 mx-auto text-nature-300 dark:text-nature-600 mb-4" />
                <p className="text-nature-500 dark:text-nature-400">
                  Ingen provisionsdata för vald period
                </p>
              </div>
            ) : (
              provisionSummaries.map(summary => (
                <div
                  key={summary.partner_id}
                  className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700"
                >
                  {/* Partner header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors"
                    onClick={() => togglePartnerExpanded(summary.partner_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedPartners.has(summary.partner_id) ? (
                          <ChevronUp className="h-5 w-5 text-nature-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-nature-400" />
                        )}
                        <div>
                          <h3 className="font-semibold text-nature-900 dark:text-nature-100">
                            {summary.partner_name}
                          </h3>
                          <p className="text-sm text-nature-500 dark:text-nature-400">
                            {summary.line_items.length} artikelrader • {summary.customer_count} kunder
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-sm text-nature-500 dark:text-nature-400">
                            Provisionsgrundande: {Math.round(summary.total_provision_base).toLocaleString('sv-SE')} kr
                          </div>
                          <div className="font-semibold text-lg text-amber-600 dark:text-amber-400">
                            Provision: {Math.round(summary.total_provision).toLocaleString('sv-SE')} kr
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSendReport(summary)
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
                        >
                          <Download className="h-4 w-4" />
                          <span className="hidden sm:inline">Skicka underlag</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded detail table */}
                  {expandedPartners.has(summary.partner_id) && (
                    <div className="border-t border-nature-200 dark:border-nature-700 overflow-x-auto">
                      <table className="min-w-full divide-y divide-nature-200 dark:divide-nature-700">
                        <thead className="bg-nature-50 dark:bg-nature-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Uppdrag</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Datum</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Kund</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Artikel</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Antal</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Enhet</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">À-pris</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Summa</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Provision</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
                          {summary.line_items.map((item, idx) => (
                            <tr key={idx} className="hover:bg-nature-50 dark:hover:bg-nature-700/30">
                              <td className="px-4 py-3 text-sm">
                                <button
                                  onClick={() => navigate(`/admin/assignments/${item.assignment_id}`)}
                                  className="text-green-700 dark:text-green-400 hover:underline cursor-pointer"
                                >
                                  #{item.assignment_number || '-'}
                                </button>
                              </td>
                              <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">{item.start_date}</td>
                              <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">{item.customer_name}</td>
                              <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-400">{item.article_name}</td>
                              <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{item.quantity}</td>
                              <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-400">{item.unit || 'st'}</td>
                              <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{item.unit_price.toLocaleString('sv-SE')} kr</td>
                              <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{item.total_price.toLocaleString('sv-SE')} kr</td>
                              <td className="px-4 py-3 text-sm font-medium text-amber-600 dark:text-amber-400 text-right">{Math.round(item.provision_amount).toLocaleString('sv-SE')} kr</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-nature-50 dark:bg-nature-900/50">
                          <tr>
                            <td colSpan={7} className="px-4 py-3 text-sm font-medium text-nature-900 dark:text-nature-100 text-right">Totalt:</td>
                            <td className="px-4 py-3 text-sm font-medium text-nature-900 dark:text-nature-100 text-right">{Math.round(summary.total_provision_base).toLocaleString('sv-SE')} kr</td>
                            <td className="px-4 py-3 text-sm font-bold text-amber-600 dark:text-amber-400 text-right">{Math.round(summary.total_provision).toLocaleString('sv-SE')} kr</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'partners' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => openPartnerModal()}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Ny partner
            </button>
          </div>

          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
            <table className="min-w-full divide-y divide-nature-200 dark:divide-nature-700">
              <thead className="bg-nature-50 dark:bg-nature-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Namn</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">E-post</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Procent</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Kunder</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Åtgärder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
                {partners.map(partner => (
                  <tr key={partner.id} className="hover:bg-nature-50 dark:hover:bg-nature-700/30">
                    <td className="px-4 py-3 text-sm font-medium text-nature-900 dark:text-nature-100">{partner.name}</td>
                    <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-400">{partner.email || '-'}</td>
                    <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{partner.percentage}%</td>
                    <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{customerCounts[partner.id] || 0}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        partner.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                          : 'bg-nature-100 dark:bg-nature-700 text-nature-500 dark:text-nature-400'
                      }`}>
                        {partner.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openPartnerModal(partner)}
                          className="p-1.5 text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 rounded transition-colors"
                          title="Redigera"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePartner(partner)}
                          disabled={customerCounts[partner.id] > 0}
                          className={`p-1.5 rounded transition-colors ${
                            customerCounts[partner.id] > 0
                              ? 'text-nature-300 dark:text-nature-600 cursor-not-allowed'
                              : 'text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                          title={customerCounts[partner.id] > 0 ? 'Kan inte ta bort - har kopplade kunder' : 'Ta bort'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {partners.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-nature-500 dark:text-nature-400">
                      Inga provisionspartners ännu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="space-y-4">
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow border border-nature-200 dark:border-nature-700 overflow-hidden">
            <table className="min-w-full divide-y divide-nature-200 dark:divide-nature-700">
              <thead className="bg-nature-50 dark:bg-nature-900/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Partner</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Period</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Underlagsnr</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Belopp</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Fakturanr</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Åtgärder</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
                {payouts.map(payout => {
                  const periodRef = `PROV-${payout.period_year}-${String(payout.period_month).padStart(2, '0')}`
                  const hasInvoice = !!payout.invoice_received_at
                  const isSent = !!payout.report_sent_at
                  const canDelete = !isSent

                  return (
                    <tr key={payout.id} className="hover:bg-nature-50 dark:hover:bg-nature-700/30">
                      <td className="px-4 py-3 text-sm font-medium text-nature-900 dark:text-nature-100">{payout.partner_name}</td>
                      <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">{formatPeriod(payout.period_year, payout.period_month)}</td>
                      <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-400 font-mono">{periodRef}</td>
                      <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{Math.round(payout.amount).toLocaleString('sv-SE')} kr</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          hasInvoice
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                            : isSent
                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                            : 'bg-nature-100 dark:bg-nature-700 text-nature-500 dark:text-nature-400'
                        }`}>
                          {hasInvoice ? 'Faktura mottagen' : isSent ? 'Underlag skickat' : 'Utkast'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-400">{payout.invoice_number || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {payout.report_data && (
                            <button
                              onClick={() => handleViewReport(payout)}
                              className="p-1.5 text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-200 hover:bg-nature-100 dark:hover:bg-nature-700 rounded transition-colors"
                              title="Ladda ner underlag"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          )}
                          {payout.report_data && !isSent && (
                            <button
                              onClick={() => handleMarkAsSent(payout)}
                              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                              title="Markera som skickad"
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                          )}
                          {isSent && !hasInvoice && (
                            <button
                              onClick={() => openInvoiceModal(payout)}
                              className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                              title="Registrera mottagen faktura"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletePayout(payout)}
                            disabled={!canDelete}
                            className={`p-1.5 rounded transition-colors ${
                              canDelete
                                ? 'text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20'
                                : 'text-nature-300 dark:text-nature-600 cursor-not-allowed'
                            }`}
                            title={canDelete ? 'Ta bort' : 'Kan inte ta bort - underlag redan skickat'}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {payouts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-nature-500 dark:text-nature-400">
                      Inga underlag skickade ännu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Partner Modal */}
      {showPartnerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {editingPartner ? 'Redigera partner' : 'Ny partner'}
              </h3>
              <button
                onClick={() => setShowPartnerModal(false)}
                className="p-1 text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Namn *</label>
                <input
                  type="text"
                  value={partnerFormData.name}
                  onChange={(e) => setPartnerFormData({ ...partnerFormData, name: e.target.value })}
                  className="input-field"
                  placeholder="T.ex. Westin Garden"
                />
              </div>

              <div>
                <label className="form-label">E-post</label>
                <input
                  type="email"
                  value={partnerFormData.email}
                  onChange={(e) => setPartnerFormData({ ...partnerFormData, email: e.target.value })}
                  className="input-field"
                  placeholder="partner@example.com"
                />
              </div>

              <div>
                <label className="form-label">Provisionsprocent *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={partnerFormData.percentage}
                    onChange={(e) => setPartnerFormData({ ...partnerFormData, percentage: parseFloat(e.target.value) || 0 })}
                    className="input-field w-24"
                    min="0"
                    max="100"
                    step="0.5"
                  />
                  <span className="text-nature-500">%</span>
                </div>
              </div>

              <div>
                <label className="form-label">Anteckningar</label>
                <textarea
                  value={partnerFormData.notes}
                  onChange={(e) => setPartnerFormData({ ...partnerFormData, notes: e.target.value })}
                  className="input-field"
                  rows={3}
                  placeholder="Intern anteckning..."
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="partnerActive"
                  checked={partnerFormData.is_active}
                  onChange={(e) => setPartnerFormData({ ...partnerFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-nature-300 rounded"
                />
                <label htmlFor="partnerActive" className="ml-2 text-sm font-medium text-nature-900 dark:text-nature-100">
                  Aktiv
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowPartnerModal(false)}
                className="btn-outline"
              >
                Avbryt
              </button>
              <button
                onClick={savePartner}
                className="btn-primary"
              >
                {editingPartner ? 'Spara' : 'Skapa'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
                {editingPayoutId ? 'Registrera mottagen faktura' : 'Registrera faktura'}
              </h3>
              <button
                onClick={() => { setShowInvoiceModal(false); setEditingPayoutId(null) }}
                className="p-1 text-nature-500 hover:text-nature-700 dark:text-nature-400 dark:hover:text-nature-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {editingPayoutId ? (
                // Editing existing payout - show read-only info
                <div className="bg-nature-50 dark:bg-nature-900/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-nature-500 dark:text-nature-400">Period:</span>
                    <span className="font-medium text-nature-900 dark:text-nature-100">
                      {formatPeriod(invoiceFormData.period_year, invoiceFormData.period_month)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-nature-500 dark:text-nature-400">Underlagsnr:</span>
                    <span className="font-mono text-nature-900 dark:text-nature-100">
                      PROV-{invoiceFormData.period_year}-{String(invoiceFormData.period_month).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-nature-500 dark:text-nature-400">Belopp:</span>
                    <span className="font-medium text-nature-900 dark:text-nature-100">
                      {Math.round(invoiceFormData.amount).toLocaleString('sv-SE')} kr
                    </span>
                  </div>
                </div>
              ) : (
                // Creating new payout (legacy flow)
                <>
                  <div>
                    <label className="form-label">Partner *</label>
                    <select
                      value={invoiceFormData.provision_partner_id}
                      onChange={(e) => setInvoiceFormData({ ...invoiceFormData, provision_partner_id: e.target.value })}
                      className="input-field"
                    >
                      <option value="">Välj partner</option>
                      {partners.filter(p => p.is_active).map(partner => (
                        <option key={partner.id} value={partner.id}>{partner.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">År</label>
                      <input
                        type="number"
                        value={invoiceFormData.period_year}
                        onChange={(e) => setInvoiceFormData({ ...invoiceFormData, period_year: parseInt(e.target.value) || new Date().getFullYear() })}
                        className="input-field"
                        min="2020"
                        max="2100"
                      />
                    </div>
                    <div>
                      <label className="form-label">Månad</label>
                      <select
                        value={invoiceFormData.period_month}
                        onChange={(e) => setInvoiceFormData({ ...invoiceFormData, period_month: parseInt(e.target.value) })}
                        className="input-field"
                      >
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={i + 1}>{formatPeriod(invoiceFormData.period_year, i + 1)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Belopp (kr)</label>
                    <input
                      type="number"
                      value={invoiceFormData.amount}
                      onChange={(e) => setInvoiceFormData({ ...invoiceFormData, amount: parseFloat(e.target.value) || 0 })}
                      className="input-field"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="form-label">Fakturanummer *</label>
                <input
                  type="text"
                  value={invoiceFormData.invoice_number}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, invoice_number: e.target.value })}
                  className="input-field"
                  placeholder="Partnerns fakturanummer"
                />
              </div>

              <div>
                <label className="form-label">Anteckningar</label>
                <textarea
                  value={invoiceFormData.notes}
                  onChange={(e) => setInvoiceFormData({ ...invoiceFormData, notes: e.target.value })}
                  className="input-field"
                  rows={2}
                  placeholder="Anteckningar..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="btn-outline"
              >
                Avbryt
              </button>
              <button
                onClick={saveInvoice}
                className="btn-primary"
              >
                Registrera
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
